"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormulaFieldLogic = void 0;
const field_logic_1 = require("../../field-type-registry/field-logic");
const comparisons_1 = require("../../comparisons/comparisons");
const fields_1 = require("../../utils/fields");
const google_sheet_1 = require("../../utils/google-sheet");
const intl_1 = require("../../locale/intl");
const formula_fieldtype_1 = require("../../safe-field-value/calculated-fields/formula-field/formula.fieldtype");
const texts_1 = require("../../texts");
const target_field_1 = require("../../utils/target-field");
class FormulaFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.aggregates = {};
        this.name = "formulafield";
        this.label = (0, intl_1.$t)(texts_1.formulaFieldText.formula);
        this.icon = "formula";
        this.iconColor = "#3EAC40";
        this.placeholder = (0, intl_1.$t)(texts_1.formulaFieldText.enter_text_here);
        this.searchSynonyms = ["formula"];
        this.isCalculated = true;
        this.useTargetFieldMigrations = true;
        this.showConfirmationOnMigrate = true;
        this.confirmationOnMigrateMessage = (0, intl_1.$t)(texts_1.formulaFieldText.converting_this_formula_to_another_field_type);
        this.isFormula = true;
        this.fieldValueGetter = new formula_fieldtype_1.FormulaFieldValueGetter();
    }
    sortKey(fieldValue, field, userPreferences, record, structure, rule) {
        return (0, target_field_1.withTargetField)(field, "", (targetField, fieldLogic) => fieldLogic.sortKey(fieldValue, targetField, userPreferences, record, structure, rule));
    }
    valueToString(fieldValue, field, ...rest) {
        return (0, target_field_1.withTargetField)(field, "", (targetField, fieldLogic) => {
            const slug = targetField.slug;
            return fieldLogic.valueToString(fieldValue, { ...targetField, slug }, ...rest);
        });
    }
    valueToGoogleSheet(fieldValue, field, ...rest) {
        const value = this.valueToString(fieldValue, field, ...rest);
        if (isNaN(Number(value))) {
            return (0, google_sheet_1.makeRowDataValues)(value, "stringValue");
        }
        return (0, google_sheet_1.makeRowDataValues)(Number(value), "numberValue");
    }
    valueToExcelExport(fieldValue, field, userPreferences, _record) {
        if (fieldValue && !isNaN(Number(fieldValue))) {
            return {
                t: "n",
                v: String(fieldValue),
            };
        }
        return {
            t: "t",
            v: this.valueToExport(fieldValue, field, userPreferences, _record),
        };
    }
    processFieldValue(record, slug, comparison, field, userPreferences) {
        const value = (0, fields_1.getValueFromRecordByField)(record, field);
        return (0, target_field_1.withTargetField)(field, "", (targetField, targetFieldLogic) => {
            const [targetSlug] = (0, target_field_1.retrieveTargetFieldSlug)(field, targetField, slug);
            return targetFieldLogic.processFieldValue({ ...record, [targetField.slug]: value }, targetSlug, comparison, targetField, userPreferences);
        });
    }
    processFieldValueForFiltering(record, slug, comparison, field, userPreferences) {
        const value = (0, fields_1.getValueFromRecordByField)(record, field);
        return (0, target_field_1.withTargetField)(field, "", (targetField, targetFieldLogic) => {
            const [targetSlug] = (0, target_field_1.retrieveTargetFieldSlug)(field, targetField, slug);
            return targetFieldLogic.processFieldValueForFiltering({ ...record, [targetField.slug]: value }, targetSlug, comparison, targetField, userPreferences);
        });
    }
    processFilterValue(filterValue, comparison, field, userPreferences) {
        return (0, target_field_1.withTargetField)(field, "", (targetField, fieldLogic) => fieldLogic.processFilterValue(filterValue, comparison, targetField, userPreferences));
    }
    getViewControlRules(field) {
        return (0, target_field_1.withTargetField)(field, super.getViewControlRules(field), (targetField, targetFieldLogic) => targetFieldLogic.getViewControlRules(targetField));
    }
    getComparisons(field, rule) {
        return (0, target_field_1.withTargetField)(field, comparisons_1.stubTrueComparisons, (targetField, fieldLogic) => {
            const [targetSlug] = (0, target_field_1.retrieveTargetFieldSlug)(field, targetField, rule.field);
            const filterRule = { ...rule, field: targetSlug };
            return fieldLogic.getComparisons(targetField, filterRule);
        });
    }
    getSortsOptions(field) {
        return (0, target_field_1.withTargetField)(field, [], (targetField, fieldLogic) => fieldLogic.getSortsOptions(targetField));
    }
    getComparisonsOptions(field) {
        return (0, target_field_1.withTargetField)(field, [], (targetField, fieldLogic) => fieldLogic.getComparisonsOptions(targetField));
    }
    getFilterComponentData(field, comparison) {
        return (0, target_field_1.withTargetField)(field, {}, (targetField, targetFieldLogic) => {
            return {
                placeholder: targetFieldLogic.placeholder,
                field: targetField,
                ...targetFieldLogic.getFilterComponentData(targetField, comparison),
            };
        });
    }
    showSystemLockIcon(_field, view) {
        return view !== "edit-record-input";
    }
    getFieldAppTargetStructure(field) {
        return (0, target_field_1.withTargetField)(field, field, (targetField, fieldLogic) => {
            return {
                ...targetField,
                placeholder: fieldLogic.placeholder,
            };
        });
    }
    getFieldType(field) {
        var _a, _b, _c;
        return ((_c = (_b = (_a = field === null || field === void 0 ? void 0 : field.params) === null || _a === void 0 ? void 0 : _a.target_field_structure) === null || _b === void 0 ? void 0 : _b.field_type) !== null && _c !== void 0 ? _c : field.field_type);
    }
    getInlineEditRules(field) {
        return {
            ...super.getInlineEditRules(field),
            allowAddRecordToGroup: false,
        };
    }
    getCommonFieldParams(field) {
        var _a, _b;
        return {
            ...(_b = (_a = field.params) === null || _a === void 0 ? void 0 : _a.target_field_structure) === null || _b === void 0 ? void 0 : _b.params,
            ...super.getCommonFieldParams(field),
            system: false,
            primary: false,
        };
    }
    canChangeValue() {
        return false;
    }
    getNestedFields(field) {
        return (0, target_field_1.withTargetField)(field, [], (targetField, targetFieldLogic) => targetFieldLogic.getNestedFields(targetField));
    }
    filterField(field) {
        return (0, target_field_1.withTargetField)(field, field, (_targetField, targetFieldLogic) => targetFieldLogic.filterField(field));
    }
    getEmptyFilterValue(field, comparison) {
        return (0, target_field_1.withTargetField)(field, null, (targetField, targetFieldType) => {
            return targetFieldType.getEmptyFilterValue(targetField, comparison);
        });
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 90,
            minWidth: 45,
        };
    }
    isSupportedCalendarField(field) {
        return (0, fields_1.actsLikeDate)(field);
    }
    prepareFieldDataToClipboard(field) {
        const defaultData = super.prepareFieldDataToClipboard(field);
        return (0, target_field_1.withTargetField)(field, defaultData, (targetField, targetFieldLogic) => targetFieldLogic.prepareFieldDataToClipboard(targetField));
    }
    isSupportedByBulkAction() {
        return false;
    }
    getFieldValue(field, record) {
        return (0, target_field_1.withTargetField)(field, undefined, (targetField, fieldLogic) => {
            targetField.slug = field.slug;
            return fieldLogic.getFieldValue(targetField, record);
        });
    }
    isValueEmpty(fieldValue, field) {
        if (field.params.target_field_structure === null) {
            return true;
        }
        return (0, target_field_1.withTargetField)(field, true, (targetField, fieldLogic) => {
            return fieldLogic.isValueEmpty(fieldValue, targetField);
        });
    }
    progressWidgetGoalValueToString(fieldValue, field, userPreferences) {
        return (0, target_field_1.withTargetField)(field, "0", (targetField, targetFieldLogic) => targetFieldLogic.progressWidgetGoalValueToString(fieldValue, targetField, userPreferences));
    }
    metricWidgetValueToString(fieldValue, field, userPreferences) {
        return (0, target_field_1.withTargetField)(field, "0", (targetField, targetFieldLogic) => targetFieldLogic.metricWidgetValueToString(fieldValue, targetField, userPreferences));
    }
}
exports.FormulaFieldLogic = FormulaFieldLogic;
